import useTenant from "@/hooks/useTenant";
import { UserManagementContext } from "./UserManagementContext";
import { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import useUserManagement from "@/hooks/useUserManagement";
import { InvitedUser, UserWithRole } from "@/models/users/User";
import { Pagination } from "@/models/base/Pagination";
import type {
  InvitedUserSortBy,
  UserSortBy,
} from "@/services/userManagementService";
import type { GroupSortBy } from "@/pages/settings/user-management/utils/sortGroups";
import type { OrphanedWorkspaceSortBy } from "@/services/workspaceServices";
import { useTableSort } from "@/hooks/useTableSort";
import { tableLoadingState } from "@/pages/settings/user-management/utils/tableLoadingState";

interface UserManagementProviderProps {
  children: React.ReactNode;
}
const UserManagementProvider: React.FC<UserManagementProviderProps> = ({
  children,
}) => {
  const { useQueryCurrent } = useTenant();
  const { user: authUser } = useAuth0();
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [invitedUsers, setInvitedUsers] = useState<Pagination<InvitedUser>>({
    pageNumber: 0,
    data: [] as InvitedUser[],
    totalPages: 0,
    totalCount: 0,
    hasMore: false,
  });

  const { useQueryUsersWithRoles, useQueryInvitedUsers } = useUserManagement();
  const [openInviteUserDialog, setOpenInviteUserDialog] = useState(false);
  const [openCreateGroupDialog, setOpenCreateGroupDialog] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());

  // Zero-based page index, as the pagination control expects. The API is one-based.
  const [existingUsersPageNumber, setExistingUsersPageNumber] = useState(0);
  const [existingUsersPageSize, setExistingUsersPageSize] = useState(20);
  const [existingUsersPageCount, setExistingUsersPageCount] = useState(1);
  const [existingUsersSearch, setExistingUsersSearch] = useState("");
  const existingUsersSort = useTableSort<UserSortBy>("name");
  const invitedUsersSort = useTableSort<InvitedUserSortBy>("email");
  const groupsSort = useTableSort<GroupSortBy>("name");
  const orphanedWorkspacesSort = useTableSort<OrphanedWorkspaceSortBy>("Name");

  const [groupsSearch, setGroupsSearch] = useState("");
  const [orphanedWorkspacesSearch, setOrphanedWorkspacesSearch] = useState("");

  const [invitedUsersPageNumber, setInvitedUsersPageNumber] = useState(0);
  const [invitedUsersPageSize, setInvitedUsersPageSize] = useState(100);

  const tenantQuery = useQueryCurrent();
  const usersQuery = useQueryUsersWithRoles(
    existingUsersPageNumber + 1,
    existingUsersPageSize,
    existingUsersSearch || undefined,
    existingUsersSort.sortBy,
    existingUsersSort.sortAscending
  );
  const invitedUsersQuery = useQueryInvitedUsers(
    invitedUsersPageNumber + 1,
    invitedUsersPageSize,
    invitedUsersSort.sortBy,
    invitedUsersSort.sortAscending
  );

  const tenant = useMemo(() => {
    return tenantQuery.data || null;
  }, [tenantQuery.data]);

  const usersState = tableLoadingState(usersQuery);
  const invitedUsersState = tableLoadingState(invitedUsersQuery);

  useEffect(() => {
    if (invitedUsersQuery.isFetched && invitedUsersQuery.data) {
      setInvitedUsers(invitedUsersQuery.data);
    }
  }, [invitedUsersQuery.isFetched, invitedUsersQuery.data]);

  useEffect(() => {
    if (usersQuery.isFetched && usersQuery.data) {
      setUsers(usersQuery.data.data);
      setExistingUsersPageCount(usersQuery.data.totalPages);
      // The API echoes a one-based page number; the pagination control is zero-based.
      setExistingUsersPageNumber(usersQuery.data.pageNumber - 1);
    }
  }, [usersQuery.isFetched, usersQuery.data]);

  useEffect(() => {
    setExistingUsersPageNumber(0);
  }, [existingUsersPageCount]);

  // Reset to first page when the search or the sort changes
  useEffect(() => {
    setExistingUsersPageNumber(0);
  }, [
    existingUsersSearch,
    existingUsersSort.sortBy,
    existingUsersSort.sortAscending,
  ]);

  useEffect(() => {
    setInvitedUsersPageNumber(0);
  }, [invitedUsersSort.sortBy, invitedUsersSort.sortAscending]);
  return (
    <UserManagementContext.Provider
      value={{
        users,
        invitedUsers,
        authUser,
        tenant,
        openInviteUserDialog,
        setOpenInviteUserDialog,
        openCreateGroupDialog,
        setOpenCreateGroupDialog,
        bulkMode,
        setBulkMode,
        selectedUsers,
        setSelectedUsers,
        existingUsersPageNumber,
        setExistingUsersPageNumber,
        existingUsersPageSize,
        setExistingUsersPageSize,
        existingUsersPageCount,
        existingUsersSearch,
        setExistingUsersSearch,
        existingUsersSortBy: existingUsersSort.sortBy,
        existingUsersSortAscending: existingUsersSort.sortAscending,
        setExistingUsersSort: existingUsersSort.setSort,
        invitedUsersSortBy: invitedUsersSort.sortBy,
        invitedUsersSortAscending: invitedUsersSort.sortAscending,
        setInvitedUsersSort: invitedUsersSort.setSort,
        groupsSearch,
        setGroupsSearch,
        groupsSortBy: groupsSort.sortBy,
        groupsSortAscending: groupsSort.sortAscending,
        setGroupsSort: groupsSort.setSort,
        orphanedWorkspacesSearch,
        setOrphanedWorkspacesSearch,
        orphanedWorkspacesSortBy: orphanedWorkspacesSort.sortBy,
        orphanedWorkspacesSortAscending: orphanedWorkspacesSort.sortAscending,
        setOrphanedWorkspacesSort: orphanedWorkspacesSort.setSort,
        invitedUsersPageNumber,
        setInvitedUsersPageNumber,
        invitedUsersPageSize,
        setInvitedUsersPageSize,
        isLoadingTenant: tenantQuery.isPending,
        isLoadingUsers: usersState.showSkeleton,
        isLoadingInvitedUsers: invitedUsersState.showSkeleton,
        isRefreshingUsers: usersState.isRefreshing,
        isRefreshingInvitedUsers: invitedUsersState.isRefreshing,
      }}
    >
      {children}
    </UserManagementContext.Provider>
  );
};
export default UserManagementProvider;
